import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface ReceiptData {
  id: number;
  installation: string;
  payMethod: string;
  datePayment: string;
  amount: number;
  cardNumber?: string;
  cardName?: string;
  reference?: string;
  bookingId?: number;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
}

export interface CompanyData {
  name: string;
  currencySymbol: string;
}

const formatCurrency = (value: number, symbol: string): string => {
  return `${value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} ${symbol}`;
};

export const generatePaymentReceiptPDF = (
  receiptData: ReceiptData,
  companyData: CompanyData,
  logoBase64: string | null,
  translations: Record<string, string>
): jsPDF => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;

  // Logo (top-right corner)
  if (logoBase64) {
    try {
      const format = logoBase64.includes('image/png') ? 'PNG' : 'JPEG';
      doc.addImage(logoBase64, format, pageWidth - 55, 10, 40, 25);
    } catch {
      // Skip logo if format is unsupported
    }
  }

  // Company name
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(companyData.name, 14, yPos);
  yPos += 12;

  // Receipt title
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(translations.paymentReceipt || 'Recibo de Pago', 14, yPos);
  yPos += 8;

  // Receipt number and date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`${translations.receiptNumber || 'Recibo N°'}: ${receiptData.id}`, 14, yPos);
  yPos += 6;
  if (receiptData.bookingId) {
    doc.text(`${translations.reservationId || 'ID Reserva'}: ${receiptData.bookingId}`, 14, yPos);
    yPos += 6;
  }
  doc.text(`${translations.paymentDate || 'Fecha de pago'}: ${receiptData.datePayment}`, 14, yPos);
  yPos += 10;

  // Client info
  if (receiptData.clientName || receiptData.clientEmail || receiptData.clientPhone) {
    doc.setFont('helvetica', 'bold');
    doc.text(translations.clientInfo || 'Información del cliente', 14, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    if (receiptData.clientName) {
      doc.text(`${translations.clientName || 'Nombre'}: ${receiptData.clientName}`, 14, yPos);
      yPos += 5;
    }
    if (receiptData.clientEmail) {
      doc.text(`${translations.email || 'Correo electrónico'}: ${receiptData.clientEmail}`, 14, yPos);
      yPos += 5;
    }
    if (receiptData.clientPhone) {
      doc.text(`${translations.phone || 'Teléfono'}: ${receiptData.clientPhone}`, 14, yPos);
      yPos += 5;
    }
    yPos += 5;
  }

  // Separator
  doc.setDrawColor(200, 200, 200);
  doc.line(14, yPos, pageWidth - 14, yPos);
  yPos += 10;

  // Payment details table
  const subtotal = receiptData.amount;
  const tax = subtotal * 0.13;
  const total = subtotal + tax;
  const sym = companyData.currencySymbol;

  autoTable(doc, {
    startY: yPos,
    head: [[translations.detail || 'Detalle', '']],
    body: [
      [translations.installation || 'Instalación', receiptData.installation || ''],
      [translations.paymentMethod || 'Método de pago', receiptData.payMethod || ''],
      [translations.cardNumber || 'Número de tarjeta', receiptData.cardNumber || ''],
      [translations.cardName || 'Titular de la tarjeta', receiptData.cardName || ''],
      [translations.reference || 'Referencia', receiptData.reference || ''],
      ['', ''],
      [translations.subtotal || 'Subtotal', formatCurrency(subtotal, sym)],
      [translations.tax || 'Impuesto (13%)', formatCurrency(tax, sym)],
      [
        { content: translations.total || 'Total', styles: { fontStyle: 'bold' } },
        { content: formatCurrency(total, sym), styles: { fontStyle: 'bold' } },
      ],
    ],
    theme: 'striped',
    headStyles: { fillColor: [62, 98, 124] },
    margin: { left: 14, right: 14 },
    columnStyles: {
      0: { cellWidth: 70 },
    },
  });

  return doc;
};

export const pdfToBase64 = (doc: jsPDF): string => {
  const dataUri = doc.output('datauristring');
  return dataUri.split(',')[1];
};

export const openPdfInNewTab = (doc: jsPDF): void => {
  const blobUrl = doc.output('bloburl');
  window.open(blobUrl as unknown as string, '_blank');
};
